import { InMemoryCache, ApolloClient, HttpLink } from "@apollo/client"

import { pageSlugPrefixes } from "../store/constants"

import fragmentTypes from "./ContentfulGQL/fragmentTypes.json"

import { gql } from "@apollo/client"

import store from "@/store"

import TYPES from "./ContentfulGQL/types"

const API = new ApolloClient({
  link: new HttpLink({
    uri: `${process.env.VUE_APP_CONTENTFUL_GRAPHQL_URL}/environments/${process.env.VUE_APP_CONTENTFUL_BRANCH}`,
    headers: {
      authorization: `Bearer ${process.env.VUE_APP_CONTENTFUL_ACCESS_KEY}`
    }
  }),
  defaultOptions: {
    query: {
      errorPolicy: "all"
    }
  },
  cache: new InMemoryCache({
    possibleTypes: fragmentTypes.__schema.types.reduce((acc, type) => {
      if (type.possibleTypes)
        acc[type.name] = type.possibleTypes.map(t => t.name)
      return acc
    }, {}),
    typePolicies: {
      // The schema query doesn't return the sys field
      Entry: {
        fields: {
          sys: {
            merge(existing, incoming) {
              return { ...existing, ...incoming }
            }
          }
        }
      }
    }
  })
})

export default {
  apolloClient: API,
  TYPES,

  fetchEntriesOfContentType: async (
    contentType,
    /* locale = null, */
    limit = 500
  ) => {
    return API.query({
      query: gql`
        ${TYPES(contentType).query}
        query Contentful_fetchAllOfType ($limit: Int!, $locale: String) {
          ${
            TYPES(contentType).fieldname
          }Collection(limit: $limit, locale: $locale) {
            items {
              ...${contentType}Fields
            }
          }
        }
      `,
      variables: {
        limit,
        locale: store.getters["locale/GET_ACTIVE_LANGUAGE"]
      }
    })
      .then(result => {
        return result.data[`${TYPES(contentType).fieldname}Collection`].items
      })
      .catch(() => {
        return null
      })
  },

  fetchEntryById: async (contentType, id /* locale = null */) => {
    return API.query({
      query: gql`
        ${TYPES(contentType).query}
        query Contentful_fetchEntryById($id: String!, $locale: String) {
          ${TYPES(contentType).fieldname}(id: $id, locale: $locale) {
            ...${contentType}Fields
          }
        }
      `,
      variables: {
        id: id,
        locale: store.getters["locale/GET_ACTIVE_LANGUAGE"]
      }
    }).then(result => {
      // if (contentType == "Page")
      //   console.log("pageData in FUNC", JSON.parse(JSON.stringify(result)))
      if (result.errors) {
        Bleach.debug.warn(
          `Entry ${contentType} ${id} has some errors in response:`,
          result.errors
        )
      }
      return result.data[TYPES(contentType).fieldname]
    })
  },

  fetchMarketConfiguredNonPageEntryBySlug: async (contentType, slug) => {
    return API.query({
      query: gql`
        ${TYPES(contentType).query}
        query Contentful_fetchEntryByValue($slug: String!, $locale: String, $marketId: [String]) {
          ${TYPES(contentType).fieldname}Collection(where: { AND: [{
            slug: $slug
          },{
            marketConfig_contains_all: $marketId
          }]}, limit: 1, locale: $locale)
          {
            items {
            ...${contentType}Fields
            }
          }
        }
      `,
      variables: {
        slug,
        locale: store.getters["locale/GET_ACTIVE_LANGUAGE"],
        marketId: [store.getters["locale/GET_ACTIVE_MARKET"]?.id]
      }
    })
      .then(result => {
        return result.data[`${TYPES(contentType).fieldname}Collection`].items[0]
      })
      .catch(() => {
        return null
      })
  },

  fetchNonPageEntryBySlug: async (contentType, slug) => {
    return API.query({
      query: gql`
        ${TYPES(contentType).query}
        query Contentful_fetchEntryByValue($slug: String!, $locale: String) {
          ${TYPES(contentType).fieldname}Collection(where: {
            slug: $slug
          }, limit: 1, locale: $locale)
          {
            items {
            ...${contentType}Fields
            }
          }
        }
      `,
      variables: {
        slug,
        locale: store.getters["locale/GET_ACTIVE_LANGUAGE"]
      }
    })
      .then(result => {
        return result.data[`${TYPES(contentType).fieldname}Collection`].items[0]
      })
      .catch(() => {
        return null
      })
  },

  fetchEntryByValue: async (
    contentType,
    field,
    value /* , locale = null */
  ) => {
    // console.error("QUERY?", TYPES(contentType).query)
    return API.query({
      query: gql`
        ${TYPES(contentType).query}
        query Contentful_fetchEntryByValue($val: String!, $locale: String) {
          ${
            TYPES(contentType).fieldname
          }Collection(where: {${field}: $val}, limit: 1, locale: $locale) {
            items {
            ...${contentType}Fields
            }
          }
        }
      `,
      variables: {
        val: value,
        locale: store.getters["locale/GET_ACTIVE_LANGUAGE"]
      }
    })
      .then(result => {
        return result.data[`${TYPES(contentType).fieldname}Collection`].items[0]
      })
      .catch(() => {
        return null
      })
  },

  /* TODO: Clean this up once the GQL migration is complete */
  fetchPageEntryByPrefixedSlug: async (slug = "" /* , locale = null */) => {
    let _prefix = "VOID"
    for (const prefix in pageSlugPrefixes) {
      if (slug.indexOf(`${pageSlugPrefixes[prefix]}/`) == 0) {
        _prefix = pageSlugPrefixes[prefix]
      }
    }
    const _remainingSlug = slug
      .split(`${_prefix}/`)
      .pop() /* slug minus any valid prefix (for backward compatibility) */

    Bleach.debug.log(
      "fetching",
      slug,
      _prefix,
      _remainingSlug,
      "PREFIX OPTIONS:",
      Object.values(pageSlugPrefixes)
    )
    /* prefix and FULL slug */
    /* OR */
    /* prefix and PARTIAL slug (ie, backwards support for any pages that might have the prefix still in the slug) */
    /* OR */
    /* FULL slug only */

    return API.query({
      query: gql`
        ${TYPES("Page").query}
        query Contentful_fetchEntryByQuery($prefix: String, $prefixOptions: [String!]!, $remainingSlug: String, $slug: String!, $locale: String, $marketId: [String]) {
          ${TYPES("Page").fieldname}Collection(where: { AND: [
            {
              marketConfig_contains_all: $marketId
            },
            { OR: [
              { AND: [
                { slugPrefix: $prefix },
                { slug: $slug }
              ]},
              { AND: [
                { slugPrefix: $prefix },
                { slug: $remainingSlug }
              ]},
              { AND: [
                { slugPrefix_not_in: $prefixOptions },
                { slug: $slug }
              ] },
            ]
            }]
          }, limit: 1, locale: $locale) {
            items {
            ...PageFields
            }
          }
        }
      `,
      variables: {
        slug: slug,
        prefix: _prefix,
        prefixOptions: Object.values(pageSlugPrefixes),
        remainingSlug: _remainingSlug,
        locale: store.getters["locale/GET_ACTIVE_LANGUAGE"],
        marketId: [store.getters["locale/GET_ACTIVE_MARKET"]?.id]
      }
    })
      .then(result => {
        Bleach.debug.log("page finder", result)
        return result.data[`${TYPES("Page").fieldname}Collection`].items[0]
      })
      .catch(() => {
        return null
      })
  }
}
